import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import BdsInput from '../../../../shared/components/v3/BdsInput/BdsInput';
import BdsButton from '../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsIconButton from '../../../../shared/components/v3/Buttons/BdsIconButton/BdsIconButton';
import BdsCardContainer from '../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import ErrorCard from '../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import BdsDivider from '../../../../shared/components/v3/Globals/BdsDivider/BdsDivider';
import Toast from '../../../../shared/components/v3/Toast/Toast';
import { EDIT_NE } from '../../../../shared/constants/images';
import {
  CLICK_ACTION,
  COLLABORATOR_BENEFIT_DETAIL_CONFIRM_EDIT_RF,
  COLLABORATOR_BENEFIT_DETAIL_EDIT_RF
} from '../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../../shared/constants/toast';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { ICollaboratorDataRF, IUpdateDataRF } from '../../../../shared/interfaces/collaborators';
import Companies from '../../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_COLLABORATORS } from '../../../../shared/translations/collaborators';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';
import { GenericVoidFunction } from '../../../../shared/types/genericTypes';
import { toLocaleNumberFormat } from '../../../../shared/utils/utils';

import './EditRfData.scss';

interface IEditRfData {
  email: string;
  planId: number;
  currencySalary?: string;
  initialData: ICollaboratorDataRF | undefined;
  isLoading: boolean;
  isError: boolean;
  onDataUpdated: GenericVoidFunction;
}

const EditRfData = ({ email, planId, currencySalary, initialData, isLoading, isError, onDataUpdated }: IEditRfData) => {
  const [editMode, setEditMode] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastData, setToastData] = useState(GENERIC_ERROR_TOAST);
  const [resetState, setResetState] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [invalidForm, setInvalidForm] = useState(false);
  const [formData, setFormData] = useState<ICollaboratorDataRF>();

  const { companyData } = useContext(CompanyContext);
  const { t } = useTranslation();

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const handleCancel = () => {
    setFormData(initialData);
    setEditMode(false);
    setResetState(true);
  };

  const handleConfirm = () => {
    TagManagerService.pushEvent(CLICK_ACTION, COLLABORATOR_BENEFIT_DETAIL_CONFIRM_EDIT_RF);
    setEditMode(false);
    void updateData();
  };

  const onEditData = useCallback(() => {
    TagManagerService.pushEvent(CLICK_ACTION, COLLABORATOR_BENEFIT_DETAIL_EDIT_RF);
    setEditMode(true);
  }, []);

  const validateData = (newData: any, oldData: any): any => {
    if (newData !== oldData) {
      return newData;
    } else {
      return null;
    }
  };

  const parseCommaDecimal = (raw: string): number => {
    const normalized = String(raw).replace(',', '.');
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  const salary = validateData(formData?.salary, initialData?.salary);
  const hoursPerWeek = validateData(formData?.hoursPerWeek, initialData?.hoursPerWeek);
  const zipCode = validateData(formData?.zipCode, initialData?.zipCode);

  const disabledButton = useMemo((): boolean => {
    return (!salary && !hoursPerWeek && !zipCode) || !invalidForm;
  }, [hoursPerWeek, salary, zipCode, invalidForm]);

  const updateData = async () => {
    setUpdateLoading(true);
    try {
      const data: IUpdateDataRF = {
        email: email,
        planId: Number(planId),
        ...(salary && { salary: Number(salary) }),
        ...(hoursPerWeek && { hoursPerWeek: Number(hoursPerWeek) }),
        ...(zipCode && { zipCode })
      };
      await Companies.updateCollaboratorDataRF(data, companyData.id);
      setOpenToast(true);
      setToastData({
        title: t(TRANSLATION_COLLABORATORS.detail.formData.toast),
        icon: 'success',
        button: true,
        description: '',
        buttonText: ''
      });
    } catch (e) {
      console.error(e);
      setOpenToast(true);
      setToastData({
        ...GENERIC_ERROR_TOAST,
        title: t(GENERIC_ERROR_TOAST.title),
        description: t(GENERIC_ERROR_TOAST.description)
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const grossSalaryValidator = (value: string) => {
    const parsedValue = parseCommaDecimal(value);

    if (Number.isNaN(parsedValue) || parsedValue <= 0) {
      return t(TRANSLATION_COLLABORATORS.detail.formData.errors.moreThan0);
    }

    return null;
  };

  const workingHoursValidator = (value: string) => {
    const parsedValue = Number(value);

    if (Number.isNaN(parsedValue) || parsedValue < 1) {
      return t(TRANSLATION_COLLABORATORS.detail.formData.errors.moreThan0);
    } else if (parsedValue >= 168) {
      return t(TRANSLATION_COLLABORATORS.detail.formData.errors.workingHours);
    } else {
      return null;
    }
  };

  const zipCodeValidator = (value: string) => {
    const parsedValue = Number(value);

    if (Number.isNaN(parsedValue) || !/^\d{5}$/.test(value)) {
      return t(TRANSLATION_COLLABORATORS.detail.formData.errors.zipCode);
    }

    const firstTwoDigits = Number(value.substring(0, 2));

    if (firstTwoDigits < 1 || firstTwoDigits > 52) {
      return t(TRANSLATION_COLLABORATORS.detail.formData.errors.zipCodeFormat);
    }

    return null;
  };

  const handleValidatorInput = useCallback((type: string, value: any) => {
    if (type === 'salary') {
      return grossSalaryValidator(value);
    } else if (type === 'hoursPerWeek') {
      return workingHoursValidator(value);
    } else if (type === 'zipCode') {
      return zipCodeValidator(value);
    } else {
      return null;
    }
  }, []);

  const handleOnChangeInput = useCallback(
    (value: any, type: string) => {
      setFormData((prev) => {
        return {
          ...prev,
          [type]: type === 'salary' ? parseCommaDecimal(value) : value
        } as ICollaboratorDataRF;
      });
      const v = handleValidatorInput(type, value);
      setInvalidForm(!v);
    },
    [handleValidatorInput]
  );

  const viewData = (label: string, data: any, type: any, hint: any) => {
    const value =
      type === 'salary' && data !== undefined
        ? toLocaleNumberFormat(
            data,
            { style: 'currency', currency: currencySalary, useGrouping: true, maximumFractionDigits: 2 },
            'es-ES'
          )
        : data;

    return (
      <div className="view-data flex flex-col w-full text-start align-start">
        <span className="label">{t(label)}</span>
        <span className={`data ${data === undefined ? 'no-data' : ''}`}>{value ?? t(hint)}</span>
        <BdsDivider color="light" size="s" />
      </div>
    );
  };

  const renderInput = useCallback(
    ({ label, hintText, value, type }: any) => {
      return (
        <div className="input-container w-full flex text-start">
          {!editMode ? (
            viewData(label, value, type, hintText)
          ) : (
            <BdsInput
              labelText={t(label)}
              initValue={value}
              validator={(v) => handleValidatorInput(type, v)}
              hintText={t(hintText)}
              onChanged={(v) => handleOnChangeInput(v, type)}
              onSubmitPressed={(v) => handleOnChangeInput(v, type)}
              maxLength={type === 'zipCode' ? 5 : undefined}
              resetState={resetState}
            />
          )}
        </div>
      );
    },
    [editMode, formData, currencySalary, handleValidatorInput, handleOnChangeInput, resetState]
  );

  if (isError) {
    return (
      <div className="flex flex-col justify-center items-center w-full">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          center={true}
          action={onDataUpdated}
          className="flex flex-col justify-center items-center"
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
        />
      </div>
    );
  }

  return (
    <>
      <BdsCardContainer
        className="collaborator-employment-data-section flex flex-col w-full"
        paddingSize="padding-l"
        type="solid"
        borderRadiusSize="radius-l"
      >
        <div className="top flex flex-row justify-between w-full">
          <div className="title">{t(TRANSLATION_COLLABORATORS.detail.formData.title)}</div>
          {!editMode && (
            <BdsIconButton
              disabled={isLoading}
              icon={EDIT_NE}
              loading={updateLoading}
              action={() => onEditData()}
              size="lg"
              variant="transparent"
            />
          )}
          {editMode && (
            <div className="actions flex flex-row">
              <BdsButton action={handleCancel} size="s" variant="secondary">
                {t(TRANSLATION_COLLABORATORS.detail.formData.cancelButton)}
              </BdsButton>
              <BdsButton disabled={disabledButton} action={handleConfirm} size="s" variant="primary">
                {t(TRANSLATION_COLLABORATORS.detail.formData.saveButton)}
              </BdsButton>
            </div>
          )}
        </div>
        {isLoading || updateLoading ? (
          <Skeleton animation={false} className="skeleton" variant="rectangular" />
        ) : (
          <div className="form">
            <div className="input-wrapper salary">
              {renderInput({
                label: TRANSLATION_COLLABORATORS.detail.formData.labelGrossSalary,
                hintText: TRANSLATION_COLLABORATORS.detail.formData.placeHolderGrossSalary,
                value: formData?.salary,
                type: 'salary'
              })}
            </div>
            <div className="input-wrapper working-hours">
              {renderInput({
                label: TRANSLATION_COLLABORATORS.detail.formData.labelWorkingHours,
                hintText: TRANSLATION_COLLABORATORS.detail.formData.placeHolderWorkingHours,
                value: formData?.hoursPerWeek,
                type: 'hoursPerWeek'
              })}
            </div>
            <div className="input-wrapper zip-code">
              {renderInput({
                label: TRANSLATION_COLLABORATORS.detail.formData.labelZipCode,
                hintText: TRANSLATION_COLLABORATORS.detail.formData.placeHolderZipCode,
                value: formData?.zipCode,
                type: 'zipCode'
              })}
            </div>
          </div>
        )}
      </BdsCardContainer>
      <Toast open={openToast} handleClose={handleCloseToast} buttonAction={handleCloseToast} {...toastData} />
    </>
  );
};

export default EditRfData;
